<template lang="pug">
  .stage
    canvas(ref="canvas")
</template>

<script>

import { mapState, mapMutations } from 'vuex'  
import Raf from '@/helpers/Raf'
import Scroll from '@/helpers/Scroll'
import Gyro from '@/helpers/Gyro'
import { DEG_TO_RAD, randomDist, clamp , map } from '@/helpers/MathUtils'
import MediaService from 'src/services/MediaService'
import ResizeService from 'src/services/ResizeService'
import WebAudioService from 'src/services/WebAudioService'
import { TweenMax, ColorPropsPlugin } from 'gsap/all'
const THREE = require('three')
import SwipeableUIService from '@/services/SwipeableUIService'
 
import IntroScene from '@/components/Stage/intro'
import QuestionScene from '@/components/Stage/question'
import Timer from 'src/components/Stage/question/timer'
import BlurFbo from 'src/components/Stage/blur_fbo'
 
import bgFrag from '@/shaders/bg.frag'
import vert from '@/shaders/bg/sky.vert'

export default {

  name: 'stage',
  
  computed: {

    ...mapState('swipeup', [
      'input_state',
      'INPUT_STATES',
      'progress'
    ]),

    ...mapState('question', [
      'state',
      'STATES',
      'tap_hold_ratio',
      'timer_ratio',
      'result' 
    ]),

    ...mapState('app', [
      'firebase',
      'y',
      'loading',
      'consented'
    ])

  },

  data () {
    return {
      views: [],
      renderer: null,
      scene: null,
      camera: null,
      scroll_ratio: 0,
      scroll_y: 0,
      y_delta: 0,
      animation: {
        complete: false
      },
      initialised: false,
      swipe_progress: 0,
      render_fbo: false,

      //position
      rotx: 0,
      tg: {
        z: -2000,
        y: 800,
        rotation:  {
          y : 0,
          x: 0
        }
      },

      tweenObj: {
        gyroClamp: 1,
        startColor: '#e00241',
        endColor: '#a8417e',
        blur: {
          mixAmt: 1.0
        }
      }
    }
  },
  
  mounted () {
    this.scroll = new Scroll();
    this.setupThree();
    this.onResize();
  },
  
  methods: {

    ...mapMutations('app', [
      'setY'
    ]),

    ...mapMutations('question', [
      'setResult'
    ]),

    setupThree(){

      let width = window.innerWidth;
      let height = window.innerHeight;

      this.renderer = new THREE.WebGLRenderer({
        canvas: this.$refs.canvas 
      }); 

      //this.renderer.setPixelRatio(/*1.5*/ ) 
      TweenMax.to(this.$refs.canvas, 0.5, {opacity: 1});

      // ortho camera
      this.orthoScene = new THREE.Scene();  
      this.orthoCamera = new THREE.OrthographicCamera( width / - 2, width / 2, height / 2, height / - 2, 0, 10000 );
      this.orthoCamera.position.z = 1;

      // perspective camera
      this.bufferScene = new THREE.Scene();  
      this.bufferCamera = new THREE.PerspectiveCamera( 45, width / height, 1, 2000 );
      this.bufferCamera.position.z = this.tg.z;
      this.bufferCamera.position.y = this.tg.y;
      this.bufferScene.add( this.bufferCamera );

      this.createBg();

      Raf.add(this.onTick) 
    },

    createBg() {
      var geometry = new THREE.PlaneBufferGeometry(1, 1, 1);
      this.material = new THREE.ShaderMaterial({
        vertexShader : vert,
        fragmentShader : bgFrag,
        uniforms : {
          startColor :{ 
            value : new THREE.Color('#e00241')
          },
          endColor :{ 
            value : new THREE.Color('#a8417e')
          }
        }
      }); 
      this.startColor = new THREE.Color();
      this.endColor = new THREE.Color();
      this.orthoBg = new THREE.Mesh(geometry, this.material);
      this.orthoBg.position.z = -3000;
      this.orthoScene.add(this.orthoBg);
      this.tl = new TimelineMax(); 
      this.tl.to(this.tweenObj, 1, {
        colorProps:{
          startColor: '#5c99d2',
          endColor: '#33c8ff'
        }, 
        ease: Linear.easeNone
      });
 
    },
 
    createOrthoScene(width, height) { 

        let geometry = new THREE.PlaneBufferGeometry(1, 1, 1);
        let material = new THREE.MeshBasicMaterial({
        map: this.bufferTexture.texture,
        transparent: true,

        blending: THREE.CustomBlending, // comment this out to see what happens when NormalBlending is used 
        blendEquation: THREE.AddEquation,
        blendSrc: THREE.OneFactor,
        blendDst: THREE.OneMinusSrcAlphaFactor,
        blendSrcAlpha: THREE.OneFactor,
        blendDstAlpha: THREE.OneMinusSrcAlphaFactor

      });
      material.needsUpdate = true; 
      this.orthoPlane = new THREE.Mesh(geometry, material);
      this.orthoScene.add(this.orthoPlane);

      /* Question Timer UI */
      this.timer = new Timer(); 
      this.orthoScene.add(this.timer);
      this.views.push(this.timer);
    },

    createBufferScene(width, height) {
  
      this.bufferTexture = new THREE.WebGLRenderTarget(1024, 1024, { 
        minFilter: THREE.LinearFilter, 
        magFilter: THREE.LinearFilter 
      });

      /* Intro Scene */
      this.introScene = new IntroScene({
        renderer: this.renderer
      });
      this.bufferScene.add(this.introScene);
      this.views.push(this.introScene);

      /* Question Scene */
      this.questionScene = new QuestionScene();  
     // this.questionScene.visible = false;
      this.bufferScene.add( this.questionScene );
      this.views.push(this.questionScene);


      this.blurFBO = new BlurFbo({
        tInput: this.bufferTexture.texture,
        renderer: this.renderer
      })
      this.blurFBO.resize()
    },

    loaded(){

      let width = window.innerWidth;
      let height = window.screen.height;

      this.createBufferScene(width, height);    
      this.createOrthoScene(width, height);
  
      this.onResize();
      this.continue() 
    },

    continue(){
 
      if(!this.initialised && this.consented){ 
 
        this.updateRoute(this.$route.name);

        this.initialised = true;

        TweenMax.to(this.orthoBg.scale, 1, {
          x: window.innerWidth,
          ease: 'Expo.easeIn',
          delay: 1,
          onComplete:()=>{
            this.animation.complete = true;
          }
        })
      } 
    },

    addEvents(){

      this.scroll.on(Scroll.Events.Touch, () => {  
        this.y_delta = (this.scroll.y.delta * 0.5); 
      });
      this.scroll.on(Scroll.Events.Wheel, () => {  
        this.y_delta = (this.scroll.y.delta * 0.03); 
      });
    
      ResizeService.on(ResizeService.Events.RESIZE, this.onResize); 
    },


    /* Question Scene */

    onResize(){
  
      let wh = SwipeableUIService.vh; //window.screen.height;
      let ww = window.innerWidth; 
  

      if (this.bufferTexture) {
        this.bufferTexture.setSize(ww  *2, wh*2 );
      }

      if (this.orthoCamera) {
        this.orthoCamera.left = ww / -2;
        this.orthoCamera.right = ww / 2;
        this.orthoCamera.top = wh / 2;
        this.orthoCamera.bottom = wh / -2;
        this.orthoCamera.updateProjectionMatrix();
      }

      if (this.orthoPlane) {
        this.orthoPlane.scale.x = ww;
        this.orthoPlane.scale.y = wh;
      }

      if (this.orthoBg && this.animation.complete) {
        this.orthoBg.scale.x = ww ;
        this.orthoBg.scale.y = wh;
      }

      this.bufferCamera.aspect = ww / wh
      this.bufferCamera.updateProjectionMatrix()

      this.renderer.setSize(ww, wh);
    },

    onTick(){  

      let page_state = this.$route.name; 
      switch(page_state) {
        case 'loading': 
          this.updateLoading();
          break;
        case 'question': 
        case 'result': 
          this.questionScene.tick(this.tap_hold_ratio);
          break; 
        case 'intro': 
          this.updateIntro();
          break;
        case 'swipeup': 
          this.updateSwipe();
          break;
      }

      if(page_state === 'intro' || page_state === 'swipeup'){
        this.bufferCamera.position.z += (this.tg.z - this.bufferCamera.position.z) * 0.2; 
        this.bufferCamera.position.y += (this.tg.y - this.bufferCamera.position.y) * 0.2; 
      } 
      
      let amt = ( 1 - (this.scroll_ratio * 0.9)) * this.tweenObj.gyroClamp;
      this.tg.rotation.y = Gyro.rotation.beta * DEG_TO_RAD * 0.5 * amt;
      this.tg.rotation.x = this.rotx + Gyro.rotation.alpha * DEG_TO_RAD * 2 * amt;  

      this.bufferCamera.rotation.y += (this.tg.rotation.y - this.bufferCamera.rotation.y) * 0.006;
      this.bufferCamera.rotation.x += (this.tg.rotation.x - this.bufferCamera.rotation.x) * 0.006; 

      this.renderer.render(this.bufferScene, this.bufferCamera, this.bufferTexture);
 
      this.updateTarget(); 

      this.renderer.render(this.orthoScene, this.orthoCamera);  

    },

    updateTarget(){
      if(this.tweenObj.blur.mixAmt < 1){ 
        if(!this.render_fbo){
          this.orthoPlane.material.map = this.blurFBO.fbo.texture; 
        }
        this.blurFBO.tick(this.tweenObj.blur.mixAmt);
        this.render_fbo = true;
      } else {
        if(this.render_fbo){
          this.orthoPlane.material.map = this.bufferTexture.texture; 
        }
        this.render_fbo = false;
      }
    },

    updateSwipe(){ 
      this.swipe_progress += (this.progress - this.swipe_progress) * 0.1;
      if(this.introScene){
        this.introScene.updateSwipe(this.swipe_progress);
      }
      this.tweenObj.blur.mixAmt = map(this.swipe_progress, 0, 1, 0.01, 1);
      this.tg.z = map(this.swipe_progress, 0, 1, -2000, -200); 
      //this.tg.y = map(this.swipe_progress, 0, 1, 300, 800); 
    },

    updateIntro(){ 
 
      this.scroll_y += this.y_delta;
      let tg = this.firebase.logged_in ? 2000 : 1100; 
      this.scroll_y = clamp(this.scroll_y, 0, tg);
      this.scroll_ratio = map(clamp(this.scroll_y,  0, 2000),0, 2000, 0, 1 ); 

      if(!this.firebase.logged_in){
        let gyro_ratio = (this.scroll_y / tg) 
        this.tweenObj.gyroClamp = 1 - gyro_ratio; 
      } 

      let y_ratio = 1;
      if(this.scroll_ratio < 0.75){
        y_ratio = map(this.scroll_ratio,  0, 0.75, 0, 1); 
        y_ratio = window.Sine.easeOut.getRatio(y_ratio);  
      } else if(this.scroll_ratio >= 0.8 && this.scroll_ratio <= 0.85){ 
        y_ratio = map(this.scroll_ratio,  0.8, 0.85, 1, 0.995);  
        //this.camera.rotation.z = map(y_ratio, 1, 0.99, 0, 10 * DEG_TO_RAD); 
      } else if(this.scroll_ratio > 0.85 && this.scroll_ratio < 0.92){ 
        y_ratio = map(this.scroll_ratio,  0.85, 0.92, 0.995, 1);  
      }

      if(this.scroll_ratio > 0.55 && this.scroll_ratio < 0.7){  
        this.tweenObj.blur.mixAmt = map(this.scroll_ratio,  0.55, 0.7, 1, 0.04);  
      } else if(this.scroll_ratio <= 0.55){  
        this.tweenObj.blur.mixAmt = 1;  
      }


      let z = 0;
      let rotx = 0;
      if(this.scroll_ratio > 0.55 /*&& this.firebase.logged_in*/){
        let mappedValue = map(this.scroll_ratio, 0.55, 1, 0, 1);
        z = -(500 * window.Sine.easeIn.getRatio(mappedValue));
      } else if(this.scroll_ratio < 0.4 ){
        let mappedValue = map(this.scroll_ratio, 0, 0.4 , 1, 0); 
        z = -(200 * window.Power3.easeInOut.getRatio(mappedValue));
        rotx = -(10 * DEG_TO_RAD * window.Power3.easeInOut.getRatio(mappedValue));
      }
      this.tg.y = map(y_ratio, 0, 1, 800, -1090);  
      this.tg.z = z;   
      this.rotx = rotx; 

      this.y_delta *= 0.97;  

      if(this.introScene){ 
        this.introScene.tick(this.bufferCamera.position.y, this.scroll_ratio);  
      }
 
 
      this.setY(this.bufferCamera.position.y);

      this.updateBgColor(); 
      this.checkQuestionRoute();

    },

    updateBgColor(){
      let color_ratio = map(clamp( this.scroll_ratio, 0 ,0.5), 0, 0.5,0,1);
      this.tl.progress( color_ratio);  
      this.material.uniforms.startColor.value = new THREE.Color(this.tweenObj.startColor);
      this.material.uniforms.endColor.value = new THREE.Color(this.tweenObj.endColor);
    },

    checkQuestionRoute() {
      if(this.scroll_ratio > 0.999 && this.$route.name === 'intro'){
        this.$router.replace({name: 'dossier'})
      }
    },

    updateLoading(){ 
      if(this.loading.progress < 0.99){
        this.orthoBg.scale.y = window.screen.height * this.loading.progress;
        this.orthoBg.scale.x = 1; //window.innerWidth * this.loading.progress;
      } else {
        this.orthoBg.scale.y = window.screen.height;
      }
    },

    updateSwipeUp(){ },

    updateState(val) {    
      this.timer.updateState(val); 
      this.questionScene.updateState(val); 
    },

    updateRoute(val) {

      switch(val.name) {
        case 'loading': 
          //this.questionScene.visible = false; 
          break; 
        case 'intro':  
          this.addEvents();
          break; 
        case 'question':  

          TweenMax.to( this.bufferCamera.position , 4, {
            z: -740,
            ease: 'Expo.easeInOut'
          }) 

          TweenMax.to( this.tweenObj.blur , 4, {
            mixAmt: 1,
            ease: 'Expo.easeInOut'
          })  

          TweenMax.to( this.tweenObj , 1, {
            gyroClamp: 0,
            ease: 'Expo.easeInOut'
          }) 

          break; 
        case 'result':  
          break;
        case 'swipeup':
          this.updateIntro();
          break
      }

      for (var i = 0; i < this.views.length; i++) {
        this.views[i].updateRoute(val); 
      }
    } 
  },

  watch: {
 
    'state'(val){
      this.updateState(val);
    },

    'firebase.logged_in'(val){
      if(val){
        TweenMax.to( this.tweenObj , 1, {
          gyroClamp: 1,
          ease: 'Expo.easeInOut'
        }) 
      }
    },

    '$route'(val){
      this.updateRoute(val);
    },

    'result.index'(val){},
 
    'loading.completed'(val){ 
      if(val){
        this.loaded();
      }
    },
 
    'loading.progress'(val){ 
      if(val){
      }
    },

    'consented'(val){
      this.continue();
    } 
  }
}
</script>

<style lang="sass" scoped>
  @import "src/styles/common.sass"

  .stage
    +full()
    background-color: #000;

</style>
